import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { AlertCircle, Check, CheckCircle2, ChevronLeft, CreditCard, FileCheck2, IdCard, Landmark, UserRound, Loader2, Send } from 'lucide-react';
import { kycFlowService, setKycAssist } from '../../../../services/api';
import Snackbar from '../../../common/Snackbar';
import FieldSection, { GroupDef } from './FieldSection';
import NomineesSection from './NomineesSection';
import AckSection, { ACK_LABELS, SectionAck, unitAckLabel } from './AckSection';
import ProfileHome from './ProfileHome';
import { AckSectionKey, Chip, Flow, SectionKey, ackedStatus, inputCls } from './shared';

// Investor profile — one page for KYC, bank, unit-wise nominees and the final
// acknowledgment (/investor-profile?token=…). The token comes from the secure link an
// employee sends; every rule shown here is enforced again by routers/kyc_flow.py.
// It follows the three onboarding messages (flow.step): 1 KYC only; 2 acknowledge KYC,
// add bank + one nominee per unit; 3 "Confirm and complete".

const STATES = ['Andhra Pradesh', 'Telangana', 'Karnataka', 'Tamil Nadu', 'Kerala', 'Maharashtra', 'Odisha', 'Gujarat', 'Madhya Pradesh', 'Uttar Pradesh', 'West Bengal', 'Rajasthan', 'Punjab', 'Haryana', 'Bihar', 'Delhi', 'Other'];

// Personal details; then Aadhaar — both images first, OCR fills in the details below them;
// then PAN (image and number) — required from 18, optional below (kycRequired / isAdult, same rule as the server).
const KYC_GROUPS: GroupDef[] = [
    { title: 'Personal', heading: 'Personal details', icon: <UserRound size={22} />, fields: [
        { key: 'first_name', label: 'First name (as on Aadhaar)', kind: 'name', placeholder: 'Enter first name' },
        { key: 'last_name', label: 'Last name', kind: 'name', placeholder: 'Enter last name' },
        { key: 'email', label: 'Email', kind: 'email', placeholder: 'name@example.com' },
        { key: 'occupation', label: 'Occupation', kind: 'text', placeholder: 'Enter your occupation' },
    ] },
    { title: 'Aadhaar', heading: 'Aadhaar details', icon: <IdCard size={22} />, fields: [
        { key: 'doc_aadhar_front', label: 'Aadhaar — front', kind: 'doc', folder: 'kyc', ocr: 'aadhaar_front' },
        { key: 'doc_aadhar_back', label: 'Aadhaar — back', kind: 'doc', folder: 'kyc', ocr: 'aadhaar_back' },
        { key: 'aadhar_number', label: 'Aadhaar number', kind: 'aadhar', placeholder: '12-digit number' },
        { key: 'dob', label: 'Date of birth', kind: 'date' },
        { key: 'gender', label: 'Gender', kind: 'select', options: ['Female', 'Male', 'Other'] },
        { key: 'pincode', label: 'PIN code', kind: 'pincode', placeholder: '6 digits', hint: 'City and state fill in automatically' },
        { key: 'state', label: 'State', kind: 'select', options: STATES },
        { key: 'city', label: 'City / town', kind: 'text', placeholder: 'Enter city or town' },
        { key: 'aadhar_address', label: 'Address as on Aadhaar', kind: 'textarea', placeholder: 'Exactly as printed on your Aadhaar card' },
        { key: 'permanent_address', label: 'Permanent address', kind: 'textarea', placeholder: 'Enter Permanent address',
            sameAs: [{ key: 'aadhar_address', label: 'Same as Aadhaar address' }] },
        { key: 'current_address', label: 'Current address', kind: 'textarea', placeholder: 'Enter Current address',
            sameAs: [{ key: 'permanent_address', label: 'Same as permanent address' }, { key: 'aadhar_address', label: 'Same as Aadhaar address' }] },
    ] },
    { title: 'PAN card', heading: 'PAN card details', icon: <CreditCard size={22} />, fields: [
        { key: 'doc_pan_card', label: 'PAN card', kind: 'doc', folder: 'kyc', ocr: 'pan_card' },
        { key: 'pan_number', label: 'PAN', kind: 'pan', placeholder: 'ABCDE1234F', hint: 'Optional if you are under 18' },
    ] },
];

// City and state from a 6-digit PIN code (India Post), as on the staff Fill KYC form.
// Only fills what the investor may still edit; a state outside the list is left alone.
const PincodeHelper: React.FC<{ pincode: string; editable: Set<string>; set: (k: string, v: any) => void }> = ({ pincode, editable, set }) => {
    useEffect(() => {
        if (!editable.has('pincode') || !/^[1-9]\d{5}$/.test(pincode || '')) return;
        let cancelled = false;
        fetch(`https://api.postalpincode.in/pincode/${pincode}`)
            .then(r => r.json())
            .then(data => {
                const po = data?.[0]?.Status === 'Success' ? data[0].PostOffice?.[0] : null;
                if (cancelled || !po) return;
                if (po.District && editable.has('city')) set('city', po.District);
                if (STATES.includes(po.State) && editable.has('state')) set('state', po.State);
            })
            .catch(() => { /* lookup is a convenience; the investor can type them */ });
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pincode]);
    return null;
};

// Same rule as utils/kyc_flow.is_adult: PAN is required from 18; no date yet counts as adult.
const isAdult = (dob: string) => {
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(dob || '');
    if (!m) return true;
    const t = new Date();
    const age = t.getFullYear() - +m[1] - ((t.getMonth() + 1 < +m[2] || (t.getMonth() + 1 === +m[2] && t.getDate() < +m[3])) ? 1 : 0);
    return age >= 18;
};
const kycRequired = (key: string, values: Record<string, any>) =>
    key === 'pan_number' || key === 'doc_pan_card' ? isAdult(values.dob) : undefined;

const BANK_GROUPS: GroupDef[] = [
    { title: 'Account', heading: 'Bank account', icon: <Landmark size={22} />, fields: [
        { key: 'account_holder_name', label: 'Account holder name', kind: 'name', placeholder: 'Name as on bank account', hint: 'Up to 35 characters', maxLength: 35 },
        { key: 'account_number', label: 'Account number', kind: 'account', placeholder: '9 to 18 digits' },
        { key: 'ifsc_code', label: 'IFSC code', kind: 'ifsc', placeholder: 'ABCD0123456', hint: 'Bank name fills in automatically' },
        { key: 'bank_name', label: 'Bank name', kind: 'text', placeholder: 'Enter bank name' },
    ] },
    { title: 'Proof', icon: <FileCheck2 size={22} />, required: true, note: 'At least one image should be uploaded', fields: [
        { key: 'doc_bank_passbook', label: 'Passbook front page', kind: 'doc', folder: 'bank' },
        { key: 'doc_bank_cheque', label: 'Cancelled cheque', kind: 'doc', folder: 'bank' },
    ] },
];

// The snackbar names what's wrong, not just "fix the highlighted fields": one field's own
// error (e.g. "Aadhaar number: This Aadhaar is already registered to another account"),
// or which fields when there are several. The same errors still show under each field.
const FIELD_LABEL: Record<string, string> = Object.fromEntries(
    [...KYC_GROUPS, ...BANK_GROUPS].flatMap(g => g.fields.map(f => [f.key, f.label])));
const errorText = (message: string, errors?: Record<string, string> | null) => {
    const keys = Object.keys(errors || {});
    if (!keys.length) return message;
    const label = (k: string) => FIELD_LABEL[k] || k.replace(/_/g, ' ');
    if (keys.length === 1) return `${label(keys[0])}: ${errors![keys[0]]}`;
    return `Please fix ${keys.length} fields: ${keys.map(label).join(', ')}`;
};

const META: { key: SectionKey; n: number; label: string; desc: string }[] = [
    { key: 'kyc', n: 1, label: 'KYC details', desc: 'Step 1 · Your personal details, Aadhaar, PAN and address.' },
    { key: 'bank', n: 2, label: 'Bank details', desc: 'Step 2 · The account where your revenue credits are paid. It must be in your name.' },
    { key: 'nominees', n: 3, label: 'Unit nominees', desc: 'Step 2 · One nominee for each of your units.' },
    { key: 'ack', n: 4, label: 'Acknowledgment', desc: 'Confirm each verified section — KYC, bank and every unit’s nominee — from the link we send you.' },
];

type IfscState = 'idle' | 'loading' | 'found' | 'missing';

// What ifsc.razorpay.com returns that the investor needs to see.
interface IfscInfo { bank: string; branch: string; address: string; city: string; district: string; state: string; ifsc: string }

const IfscStatus: React.FC<{ state: IfscState; info: IfscInfo | null; className?: string }> = ({ state, info, className = '' }) => {
    if (state === 'idle') return null;
    if (state === 'found' && info) {
        const rows: [string, string][] = [
            ['Branch', info.branch], ['Address', info.address], ['City', info.city],
            ['District', info.district], ['State', info.state], ['IFSC', info.ifsc],
        ].filter(([, v]) => v) as [string, string][];
        return (
            <div className={`rounded-xl border border-emerald-200 bg-emerald-50/60 p-3 ${className}`} aria-live="polite">
                <div className="flex items-center gap-2 text-[13px] font-bold text-emerald-900"><CheckCircle2 size={15} className="shrink-0" />{info.bank || 'Bank found'}</div>
                <dl className="mt-2 grid grid-cols-[72px_minmax(0,1fr)] gap-x-3 gap-y-1 text-xs">
                    {rows.map(([k, v]) => (
                        <React.Fragment key={k}>
                            <dt className="text-slate-500">{k}</dt>
                            <dd className={`text-slate-800 font-semibold break-words ${k === 'IFSC' ? 'font-mono' : ''}`}>{v}</dd>
                        </React.Fragment>
                    ))}
                </dl>
            </div>
        );
    }
    return (
        <p className={`text-[13px] font-semibold flex items-center gap-2 ${state === 'missing' ? 'text-amber-800' : 'text-emerald-800'} ${className}`}>
            {state === 'loading' ? <Loader2 size={14} className="animate-spin" /> : <AlertCircle size={14} />}
            {state === 'loading' ? 'Looking up IFSC…' : 'IFSC not found — please check it, or type the bank name'}
        </p>
    );
};

// Looks up the bank for a valid IFSC and fills the bank name. Draws nothing itself:
// the result is reported up and shown under the IFSC field.
const IfscHelper: React.FC<{ ifsc: string; editable: boolean; setBank: (v: string) => void; onState: (s: IfscState, info: IfscInfo | null) => void }> = ({ ifsc, editable, setBank, onState }) => {
    const [state, setState] = useState<IfscState>('idle');
    const [info, setInfo] = useState<IfscInfo | null>(null);
    useEffect(() => { onState(state, info); }, [state, info, onState]);
    useEffect(() => {
        if (!editable || !/^[A-Z]{4}0[A-Z0-9]{6}$/.test(ifsc || '')) { setState('idle'); return; }
        let cancelled = false;
        setState('loading');
        const t = setTimeout(async () => {
            try {
                const res = await fetch(`https://ifsc.razorpay.com/${ifsc}`);
                if (!res.ok) throw new Error('not found');
                const d = await res.json();
                if (cancelled) return;
                setBank(String(d.BANK || '').replace(/[^A-Za-z .&]/g, '').trim());
                const str = (v: any) => String(v ?? '').trim();
                setInfo({ bank: str(d.BANK), branch: str(d.BRANCH), address: str(d.ADDRESS), city: str(d.CITY),
                    district: str(d.DISTRICT), state: str(d.STATE), ifsc: str(d.IFSC) || ifsc });
                setState('found');
            } catch { if (!cancelled) setState('missing'); }
        }, 400);
        return () => { cancelled = true; clearTimeout(t); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ifsc, editable]);
    return null;
};

const normName = (v: any) => String(v || '').toLowerCase().replace(/[^a-z]+/g, ' ').trim();
// How closely two names match, 0–100: letters only, word order ignored, then 1 − edit
// distance / longer length. "Bhargav Pamulapati" vs "PAMULAPATI BHARGAV" → 100.
const nameMatchPct = (a: string, b: string) => {
    const key = (v: string) => normName(v).split(' ').filter(Boolean).sort().join(' ');
    const x = key(a), y = key(b);
    if (!x || !y) return 0;
    let prev = Array.from({ length: y.length + 1 }, (_, j) => j);
    for (let i = 1; i <= x.length; i++) {
        const cur = [i];
        for (let j = 1; j <= y.length; j++) cur[j] = Math.min(prev[j] + 1, cur[j - 1] + 1, prev[j - 1] + (x[i - 1] === y[j - 1] ? 0 : 1));
        prev = cur;
    }
    return Math.round((1 - prev[y.length] / Math.max(x.length, y.length)) * 100);
};

// 1024px and up (tablet landscape, desktop): home and the open section side by side.
const WIDE_QUERY = '(min-width: 1024px)';
const useWide = () => {
    const [wide, setWide] = useState(() => typeof window !== 'undefined' && window.matchMedia(WIDE_QUERY).matches);
    useEffect(() => {
        const mq = window.matchMedia(WIDE_QUERY);
        const on = () => setWide(mq.matches);
        mq.addEventListener('change', on);
        return () => mq.removeEventListener('change', on);
    }, []);
    return wide;
};

// Mobile of the dashboard user signed in on this browser, if any.
const staffSessionMobile = (): string => {
    try { return String(JSON.parse(window.localStorage.getItem('ak_dashboard_session') || 'null')?.mobile || ''); } catch { return ''; }
};
const last10 = (m: any) => String(m || '').replace(/\D/g, '').slice(-10);

const hasStaffSession = () => {
    try { return !!JSON.parse(window.localStorage.getItem('ak_dashboard_session') || 'null')?.mobile; } catch { return false; }
};

// embedded: opened inside the dashboard by staff (KycQueue/AssistedFillPage) — always
// assisted entry, and the dashboard supplies the way back.
const InvestorProfilePage: React.FC<{ embedded?: boolean }> = ({ embedded = false }) => {
    const [params] = useSearchParams();
    const token = params.get('token') || '';
    // ank_verify links name what they ask to confirm: ?ack=kyc|bank|nominees (&unit=<unit_id>).
    const ackParam = params.get('ack');
    const ackUnit = params.get('unit');
    // The step-1 WhatsApp link adds ?open=kyc so it opens on the KYC form, not home.
    const openParam = params.get('open');
    // ?assist=1 from the staff verification page: a signed-in staff member fills this in
    // for an investor who can't. The server re-checks staff access on every call.
    const assistRequested = (embedded || params.get('assist') === '1') && hasStaffSession();
    // Set and cleared in one effect, declared before load() so its requests carry the
    // assist header. (Setting it during render and clearing it on unmount lost it on
    // React's dev re-mount: the second load went out unassisted.)
    useEffect(() => {
        setKycAssist(assistRequested);
        return () => setKycAssist(false);
    }, [assistRequested]);
    const [flow, setFlow] = useState<Flow | null>(null);
    const [loadError, setLoadError] = useState<string | null>(null);
    const [active, setActive] = useState<SectionKey>('kyc');
    // Opens on the home overview; tapping a section card shows that section.
    const [home, setHome] = useState(true);
    const [saving, setSaving] = useState(false);
    const [serverErrors, setServerErrors] = useState<Record<string, string>>({});
    const [snack, setSnack] = useState<{ message: string | null; type: 'success' | 'error' | null }>({ message: null, type: null });
    const [accConfirm, setAccConfirm] = useState('');
    const [ifsc, setIfsc] = useState<{ state: IfscState; info: IfscInfo | null }>({ state: 'idle', info: null });
    const onIfscState = useCallback((state: IfscState, info: IfscInfo | null) => setIfsc({ state, info }), []);
    // Last successful KYC/bank save, for the "Draft saved …" note under the mobile save bar.
    const [savedAt, setSavedAt] = useState<number | null>(null);
    const wide = useWide();
    // A unit's nominee form is open: its Save / Cancel are the only buttons shown.
    const [nomEditing, setNomEditing] = useState(false);
    const [, setTick] = useState(0);
    useEffect(() => { const t = setInterval(() => setTick(n => n + 1), 30000); return () => clearInterval(t); }, []);

    const toast = useCallback((message: string, type: 'success' | 'error' = 'success') => setSnack({ message, type }), []);
    const closeSnack = useCallback(() => setSnack({ message: null, type: null }), []);

    const load = useCallback(async () => {
        if (!token) { setLoadError('This page needs the secure link we sent you. Please open it again from your message.'); return; }
        const res = await kycFlowService.get(token);
        if (res.error) { setLoadError(res.error); return; }
        const f: Flow = res.data.flow;
        setFlow(f);
        // Land on what the current step's message asked for.
        const order: SectionKey[] = ['kyc', 'bank', 'nominees', 'ack'];
        const firstFix = order.find(k => f.sections[k] === 'needs_correction');
        let land: SectionKey = 'kyc';
        if (f.step === 'bank_nominees') {
            land = ['ready', 'sent'].includes(f.acks?.kyc?.status || '') ? 'kyc'
                : (['bank', 'nominees'] as SectionKey[]).find(k => ['draft', 'missing'].includes(f.sections[k])) || 'bank';
        } else if (f.step === 'confirm' || f.step === 'complete') land = 'ack';
        // Opened from an acknowledgment link: straight to what it asks to confirm.
        const asked = (['kyc', 'bank', 'nominees'] as SectionKey[]).find(k => k === ackParam);
        if (asked && !firstFix) { setActive(asked); setHome(false); return; }
        // Opened from Template 1's "Complete KYC" button (?open=kyc): straight to the KYC form.
        if (openParam === 'kyc') { setActive('kyc'); setHome(false); return; }
        setActive(firstFix || land);
    }, [token, ackParam, openParam]);
    useEffect(() => { load(); }, [load]);
    // ?ack= / ?open= pick the first section once; drop them from the address bar so a
    // refresh opens home instead of jumping back there. Not through the router: that
    // would reload the flow.
    useEffect(() => {
        const url = new URL(window.location.href);
        if (!url.searchParams.has('ack') && !url.searchParams.has('open')) return;
        url.searchParams.delete('ack'); url.searchParams.delete('open');
        window.history.replaceState(window.history.state, '', url.pathname + url.search + url.hash);
    }, []);

    const applyFlow = (f: Flow, msg?: string) => { setFlow(f); setServerErrors({}); if (msg) toast(msg); };

    const idx = META.findIndex(m => m.key === active);
    // Bank and nominees open with step 2, once KYC is verified. The server marks them
    // "locked" only while nothing is on file — details already given stay viewable
    // (e.g. KYC re-checked after a staff edit).
    // Nominees stay closed until the investor has a unit (an order) to name them for.
    // Bank and nominees both open once KYC is submitted and an order is placed (the
    // server stops marking them "locked").
    const gated = (k: SectionKey) => (k === 'nominees' && !flow?.units.length)
        || ((k === 'bank' || k === 'nominees') && flow?.sections[k] === 'locked');
    const go = (k: SectionKey) => { if (gated(k)) return; setActive(k); setHome(false); setServerErrors({}); window.scrollTo({ top: 0, behavior: 'smooth' }); };
    const goNext = () => { const n = META[idx + 1]; if (n) go(n.key); };

    const saveSection = async (section: 'kyc' | 'bank', values: Record<string, any>, thenSubmit = false) => {
        if (!flow) return;
        const submitSection = section === 'bank' ? 'bank' : undefined;
        if (thenSubmit && !Object.keys(values).length) { submit(submitSection); return; }
        if (!Object.keys(values).length) { toast('No changes to save'); goNext(); return; }
        setSaving(true);
        const res = section === 'kyc' ? await kycFlowService.saveKyc(token, values) : await kycFlowService.saveBank(token, values);
        setSaving(false);
        if (res.error) { setServerErrors(res.errors || {}); toast(errorText(res.error, res.errors), 'error'); return; }
        const wasDraft = flow.stage === 'DRAFT' || (section === 'bank' && flow.bank_open);
        setSavedAt(Date.now());
        if (thenSubmit) { setFlow(res.data.flow); submit(submitSection); return; }
        applyFlow(res.data.flow, wasDraft ? `${section === 'kyc' ? 'KYC' : 'Bank'} details saved` : 'Sent for review');
        if (wasDraft) goNext();
    };

    // KYC in step 1; then bank, and each unit's nominee, on its own.
    const submit = async (section?: 'bank' | 'nominees', unitId?: string) => {
        setSaving(true);
        const res = await kycFlowService.submit(token, section, unitId);
        setSaving(false);
        if (res.error) {
            // What's still missing goes in the snackbar, by the labels the investor sees.
            const d = res.details || {};
            const missing = [...(d.kyc_missing || []), ...(d.bank_missing || [])].map((k: string) => FIELD_LABEL[k] || k.replace(/_/g, ' '));
            toast(missing.length ? `${res.error}. Still missing: ${missing.join(', ')}` : res.error, 'error');
            return;
        }
        applyFlow(res.data.flow, res.data.message || 'Submitted for verification');
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    // One section, or one unit's nominee (unitId) — each has its own acknowledgment link.
    const acknowledge = async (section: AckSectionKey, version: string, unitId?: string) => {
        if (!flow) return;
        setSaving(true);
        const res = await kycFlowService.acknowledge(token, version, section, unitId);
        setSaving(false);
        if (res.error) { toast(res.error, 'error'); if (res.code === 'VERSION_CHANGED') load(); return; }
        const u = unitId ? flow.units.find(x => x.unit_id === unitId) : undefined;
        const many = !unitId && section === 'nominees' && (res.data.unit_ids || []).length > 1;
        applyFlow(res.data.flow, res.data.completed ? 'Thank you — your profile is complete'
            : many ? `Nominees for ${res.data.unit_ids.length} units acknowledged` : `${u ? unitAckLabel(u) : ACK_LABELS[section]} acknowledged`);
        if (res.data.completed) go('ack');
    };

    const investorAadhaar = useMemo(() => flow?.kyc_fields.find(r => r.key === 'aadhar_number')?.value, [flow]);

    if (loadError) {
        return (
            <div className="min-h-screen bg-[#F3F6F4] grid place-items-center p-4">
                <div className="bg-white rounded-3xl p-8 max-w-sm w-full text-center shadow-xl border border-slate-100">
                    <AlertCircle size={36} className="mx-auto text-red-600" />
                    <h1 className="text-lg font-extrabold mt-3">We couldn't open your profile</h1>
                    <p className="text-sm text-slate-600 mt-1">{loadError}</p>
                </div>
            </div>
        );
    }
    if (!flow) {
        return <div className="min-h-screen bg-[#F3F6F4] grid place-items-center"><Loader2 className="animate-spin text-[#1B4332]" size={32} /></div>;
    }

    const name = [flow.investor.first_name, flow.investor.last_name].filter(Boolean).join(' ');
    const stage = flow.stage;
    const assist = !!flow.assist;
    const s = flow.sections;
    const core: SectionKey[] = ['kyc', 'bank', 'nominees'];
    const fixCount = core.filter(k => s[k] === 'needs_correction').length;
    const step = flow.step;
    const units = flow.units.length;
    const kycAckPending = ['ready', 'sent'].includes(flow.acks?.kyc?.status || '');
    // Step 1 submits KYC from the block below. Bank submits from its own form, and each
    // unit's nominee from its card — each edits like a draft until submitted.
    const showSubmit = step === 'kyc' && stage === 'DRAFT';
    const canSubmit = flow.can_submit;
    const bankStage = flow.bank_open ? 'DRAFT' : stage;
    // Before KYC is submitted only bank fields staff flagged can change — the server
    // refuses the rest — so empty ones don't show as fillable either.
    const bankRows = step === 'kyc' && !flow.bank_open ? flow.bank_fields.map(r => ({ ...r, empty: false })) : flow.bank_fields;
    // Account holder name starts as the KYC name (first + last); the investor can edit it.
    const kycVal = (k: string) => String(flow.kyc_fields.find(r => r.key === k)?.value ?? flow.investor[k as 'first_name' | 'last_name'] ?? '').trim();
    const holderName = [kycVal('first_name'), kycVal('last_name')].filter(Boolean).join(' ');
    const submitLabel = 'Submit KYC for verification';
    let banner: { cls: string; title: string; text: string; action?: { label: string; go: SectionKey } } = {
        cls: 'bg-emerald-50 text-emerald-950', title: 'Step 1 of 3 · Share your KYC details',
        text: 'Fill in your details and upload your Aadhaar and PAN photos, then submit. Your bank and nominee details open once you submit.',
    };
    if (stage !== 'ACKNOWLEDGED' && fixCount) {
        const first = core.find(k => s[k] === 'needs_correction') as SectionKey;
        banner = { cls: 'bg-red-50 text-red-950', title: `${fixCount} section${fixCount > 1 ? 's need' : ' needs'} correction`, text: 'Fix only the flagged items — everything already verified stays locked.', action: { label: 'Fix now', go: first } };
    } else if (step === 'kyc' && stage !== 'DRAFT') {
        // Bank and nominees open while KYC is in review.
        banner = flow.bank_open || flow.nominees_open
            ? { cls: 'bg-amber-50 text-amber-950', title: 'Step 1 of 3 · We’re verifying your KYC', text: 'Meanwhile, add your bank account for revenue credits and your nominee details, and submit them.', action: flow.bank_open ? { label: 'Add bank details', go: 'bank' } : { label: 'Add nominee details', go: 'nominees' } }
            : { cls: 'bg-amber-50 text-amber-950', title: 'Step 1 of 3 · We’re verifying your KYC', text: 'We’ll message you on WhatsApp once it’s done.' };
    } else if (step === 'bank_nominees' && kycAckPending) {
        banner = { cls: 'bg-[#1B4332] text-white', title: 'Good news — your KYC is verified', text: `Please review and acknowledge your KYC details, then add your ${flow.bank_open ? 'bank and nominee' : 'nominee'} details.`, action: { label: 'Review KYC', go: 'kyc' } };
    } else if (step === 'bank_nominees' && (flow.details_open || core.some(k => s[k] === 'missing'))) {
        const first = (['bank', 'nominees'] as SectionKey[]).find(k => ['draft', 'missing'].includes(s[k])) || 'bank';
        const bankLeft = flow.bank_open, nomLeft = flow.nominees_open && units > 0;
        banner = bankLeft && nomLeft
            ? { cls: 'bg-emerald-50 text-emerald-950', title: 'Step 2 of 3 · Add your bank and nominee details', text: `Your bank account for revenue credits, and one nominee for each of your ${units} unit${units === 1 ? '' : 's'}. Submit each when it’s done.`, action: { label: 'Add details', go: first } }
            : nomLeft
                ? { cls: 'bg-emerald-50 text-emerald-950', title: 'Step 2 of 3 · Add your nominee details', text: `${flow.bank_open ? '' : 'We’re verifying your bank details. '}Add one nominee for each of your ${units} unit${units === 1 ? '' : 's'}, and submit each unit.`, action: { label: 'Add nominees', go: 'nominees' } }
                : bankLeft
                    ? { cls: 'bg-emerald-50 text-emerald-950', title: 'Step 2 of 3 · Add your bank details', text: `${units ? 'We’re verifying your nominee details. ' : ''}Add your bank account for revenue credits, then submit.`, action: { label: 'Add bank details', go: 'bank' } }
                    : { cls: 'bg-emerald-50 text-emerald-950', title: 'Step 2 of 3 · Add your bank and nominee details', text: `Your bank account for revenue credits${units ? `, and one nominee for each of your ${units} unit${units === 1 ? '' : 's'}` : ''}.`, action: { label: 'Add details', go: first } };
    } else if (step === 'bank_nominees') {
        banner = { cls: 'bg-amber-50 text-amber-950', title: 'Step 2 of 3 · We’re verifying your bank and nominee details', text: 'We’ll message you on WhatsApp when they’re ready for your confirmation.' };
    } else if (step === 'confirm') {
        banner = { cls: 'bg-[#1B4332] text-white', title: 'Step 3 of 3 · Confirm your details', text: 'Our team verified your bank and nominee details. Confirm each one from the link we send you — check the account number and the nominee for each unit carefully.', action: { label: 'Review & confirm', go: 'ack' } };
    } else if (step === 'complete') {
        banner = { cls: 'bg-[#1B4332] text-white', title: 'Your profile is complete', text: 'You can open any section to view your verified details.' };
    }
    // Each verified unit's nominee is acknowledged on its own, in that unit's card;
    // the one the link named comes first.
    const unitsToAck = flow.units.filter(u => ['ready', 'sent'].includes(u.ack?.status || ''))
        .sort((a, b) => Number(b.unit_id === ackUnit) - Number(a.unit_id === ackUnit));
    const score: Record<string, number> = { complete: .5, draft: .2, no_units: 0, in_review: .7, needs_correction: .6, verified: 1, locked: 0, ready: .5, sent: .5, done: 1, missing: .3 };
    const progress = Math.round((META.reduce((a, m) => a + (score[s[m.key]] ?? 0), 0) / 4) * 100);
    const cur = META[idx];
    const savedNote = (() => {
        if (!savedAt) return stage === 'DRAFT' || (active === 'bank' ? flow.bank_open : flow.nominees_open) ? 'Draft saves when you tap Save' : undefined;
        const mins = Math.floor((Date.now() - savedAt) / 60000);
        return mins < 1 ? 'Draft saved just now' : `Draft saved ${mins} min ago`;
    })();
    const initials = (name || '?').split(' ').filter(Boolean).map(w => w[0]).slice(0, 2).join('').toUpperCase();
    const mobileDigits = String(flow.investor.mobile || '').replace(/\D/g, '').slice(-10);
    const prettyMobile = mobileDigits.length === 10 ? `+91 ${mobileDigits.slice(0, 5)} ${mobileDigits.slice(5)}` : flow.investor.mobile;
    // Acknowledging is the investor's own act: not in assisted entry, and not when someone
    // else is signed in to the dashboard on this browser (the server refuses it too).
    const sessionMobile = staffSessionMobile();
    const ackBlocked = assist || (!!sessionMobile && last10(sessionMobile) !== last10(flow.investor.mobile));
    const ackWaiting = (sec: AckSectionKey) => ['ready', 'sent'].includes(flow.acks?.[sec]?.status || '');
    const ackOnlyInvestor = (
        <div className="mt-6 p-4 rounded-2xl bg-slate-50 ring-1 ring-slate-200 text-sm text-slate-700">
            <b>Only the investor can acknowledge these details.</b> Send them their acknowledgment link to confirm.
        </div>
    );
    const goHome = () => { setHome(true); setServerErrors({}); window.scrollTo({ top: 0, behavior: 'smooth' }); };

    const assistBar = assist && (
        <div className="bg-amber-100 text-amber-950 border-b border-amber-200">
            <div className="max-w-6xl mx-auto px-4 sm:px-6 py-2.5 flex flex-wrap items-center gap-3 text-sm">
                <span className="flex-1 min-w-[240px]"><b>Assisted entry:</b> you're filling in for <b>{name}</b> ({flow.investor.mobile}) as <b>{flow.assist!.by_name}</b>. Everything you save is recorded under your name. The investor confirms each section with the acknowledgment link.</span>
                {!embedded && <a href={`/kyc-queue/${flow.investor.mobile}`} className="min-h-[36px] px-3 rounded-lg bg-white ring-1 ring-amber-300 font-bold text-[13px] inline-flex items-center">Back to verification</a>}
            </div>
        </div>
    );

    if (home && !wide) {
        return (
            <div className={`${embedded ? '' : 'min-h-screen'} bg-[#F3F6F4] text-[#14231B]`}>
                <Snackbar message={snack.message} type={snack.type} onClose={closeSnack} />
                {assistBar}
                <ProfileHome flow={flow} name={name} gated={gated} onOpen={go} />
            </div>
        );
    }

    return (
        <div className={`${embedded ? 'mt-4' : 'min-h-screen'} bg-[#F3F6F4] max-lg:bg-[#F6F8F7] text-[#14231B] lg:flex lg:items-start`}>
            <Snackbar message={snack.message} type={snack.type} onClose={closeSnack} />
            {/* Tablet landscape / desktop: home stays on the left, the open step's form on the right. */}
            <div className={`hidden lg:block w-[400px] xl:w-[440px] shrink-0 border-r border-slate-200 bg-[#F3F6F4] ${embedded ? '' : 'sticky top-0 h-screen overflow-y-auto'}`}>
                <ProfileHome flow={flow} name={name} gated={gated} onOpen={go} activeKey={active} />
            </div>
            <div className="flex-1 min-w-0">
            {/* Phones: whose details these are, on every section (wide screens show it on the left),
                then the section bar — which stays at the top while scrolling. */}
            <div className="lg:hidden flex items-center gap-3 px-4 py-3 bg-[#1B4332] text-white">
                <span className="w-10 h-10 rounded-full bg-emerald-100 text-[#1B4332] grid place-items-center text-sm font-extrabold shrink-0" aria-hidden="true">{initials}</span>
                <div className="min-w-0">
                    <div className="text-[15px] font-bold truncate">{name || 'Investor'}</div>
                    <div className="text-[13px] text-emerald-100/90 tabular-nums">{prettyMobile}</div>
                </div>
            </div>
            <div className="lg:hidden sticky top-0 z-40 bg-white border-b border-slate-200">
                <div className="flex items-center gap-2 px-2 py-2.5">
                    <button type="button" onClick={goHome} aria-label="Back to home" className="w-10 h-10 grid place-items-center rounded-full hover:bg-slate-100"><ChevronLeft size={22} /></button>
                    <h1 className="flex-1 min-w-0 truncate text-[17px] font-extrabold">{cur.label}</h1>
                    <span className="pr-2 text-[13px] font-bold text-slate-600">{cur.n} of 4</span>
                </div>
            </div>

            {assistBar}
            <main className="max-w-6xl mx-auto px-4 sm:px-6 pt-6 max-lg:pt-1 pb-36 lg:pb-12">
                {/* Only a correction request needs a banner here; the step itself is clear from home. */}
                {fixCount > 0 && stage !== 'ACKNOWLEDGED' && (
                <div className={`mb-4 p-4 rounded-2xl flex flex-wrap items-center gap-3 ${banner.cls}`}>
                    <div className="flex-1 min-w-[240px]"><b className="block">{banner.title}</b><span className="text-sm opacity-90">{banner.text}</span></div>
                    {banner.action && <button type="button" onClick={() => go(banner.action!.go)} className="min-h-[44px] px-4 rounded-xl bg-white text-[#1B4332] font-bold text-sm">{banner.action.label}</button>}
                </div>
                )}

                <div className="max-lg:mt-3 flex flex-col lg:flex-row gap-5 items-start">

                    <section className="w-full flex-1 min-w-0 bg-white rounded-2xl border border-slate-200 p-4 sm:p-6 max-lg:bg-transparent max-lg:border-0 max-lg:p-0" aria-live="polite">
                        <div className="max-lg:hidden flex items-start justify-between gap-3 flex-wrap pb-4 border-b border-slate-100">
                            <div>
                                <div className="text-xs font-bold tracking-[.08em] uppercase text-[#3F5F4C]">Section {cur.n} of 4</div>
                                <h2 className="text-xl font-extrabold mt-1">{cur.label}</h2>
                                <p className="text-sm text-slate-500 mt-1">{cur.desc}</p>
                            </div>
                            <Chip status={active === 'ack' ? s.ack : ackedStatus(s[active], flow.acks?.[active]?.status)} />
                        </div>

                        {active === 'kyc' && (
                            <FieldSection stage={stage} assist={assist} rows={flow.kyc_fields} groups={KYC_GROUPS} mobile={flow.investor.mobile}
                                saving={saving} serverErrors={serverErrors}
                                saveLabel={stage === 'DRAFT' ? 'Save & continue' : flow.kyc_fields.some(r => r.verify_state === 'flagged') ? 'Send correction' : 'Submit for review'}
                                requiredOverride={kycRequired} readOnly={s.kyc === 'verified' || !!flow.mou_locked}
                                extra={(vals, set, editable) => <PincodeHelper pincode={vals.pincode} editable={editable} set={set} />}
                                submit={step === 'kyc' && stage === 'DRAFT' && !flow.mou_locked ? { label: 'Submit for review', onSubmit: v => saveSection('kyc', v, true) } : undefined}
                                savedNote={savedNote} onSave={v => saveSection('kyc', v)} />
                        )}
                        {active === 'kyc' && !ackBlocked && <SectionAck flow={flow} section="kyc" busy={saving} onAcknowledge={acknowledge} />}
                        {active === 'kyc' && ackBlocked && ackWaiting('kyc') && ackOnlyInvestor}
                        {active === 'bank' && (
                            <FieldSection stage={bankStage} assist={assist} rows={bankRows} groups={BANK_GROUPS} readOnly={s.bank === 'verified' || !!flow.mou_locked} mobile={flow.investor.mobile}
                                prefill={{ account_holder_name: holderName }}
                                saving={saving} serverErrors={serverErrors}
                                saveLabel={bankStage === 'DRAFT' ? 'Save & continue' : flow.bank_fields.some(r => r.verify_state === 'flagged') ? 'Send correction' : 'Submit for review'}
                                extraErrors={vals => {
                                    const e: Record<string, string> = {};
                                    const accEditable = bankStage === 'DRAFT' || flow.flagged.includes('account_number') || !!flow.bank_fields.find(r => r.key === 'account_number')?.empty;
                                    const onFile = String(flow.bank_fields.find(r => r.key === 'account_number')?.value || '');
                                    const accChanged = String(vals.account_number || '') !== onFile;
                                    if (accEditable && (accChanged || bankStage !== 'DRAFT') && vals.account_number && accConfirm !== vals.account_number) e.account_confirm = 'Account numbers don’t match';
                                    if (bankStage === 'DRAFT' && !vals.doc_bank_passbook && !vals.doc_bank_cheque) e.doc_bank_passbook = 'Upload at least one image';
                                    return e;
                                }}
                                extra={(vals, set, editable) => (
                                    <>
                                        <IfscHelper ifsc={vals.ifsc_code} editable={editable.has('ifsc_code')} onState={onIfscState} setBank={b => { if (b && editable.has('bank_name')) set('bank_name', b); }} />
                                    </>
                                )}
                                fieldAfter={(key, vals, editable) => {
                                    if (key === 'account_holder_name' && editable.has(key) && vals.account_holder_name) {
                                        // A note only — the name is accepted whatever it matches.
                                        const kyc = [flow.investor.first_name, flow.investor.last_name].filter(Boolean).join(' ');
                                        if (!kyc) return null;
                                        const pct = nameMatchPct(vals.account_holder_name, kyc);
                                        return <p className={`text-[13px] font-semibold flex items-center gap-2 ${pct === 100 ? 'text-emerald-800' : pct >= 70 ? 'text-amber-800' : 'text-red-700'}`}>
                                            {pct === 100 ? <Check size={14} strokeWidth={3} /> : <AlertCircle size={14} />}{pct}% match with your KYC name ({kyc})</p>;
                                    }
                                    if (key === 'account_number' && editable.has(key)) {
                                        const bad = accConfirm !== vals.account_number;
                                        return (
                                            <>
                                                <label htmlFor="acc-confirm-m" className="block text-[13px] font-bold text-slate-700 mb-1.5">Re-enter account number<span className="text-red-600 ml-0.5">*</span></label>
                                                <input id="acc-confirm-m" inputMode="numeric" autoComplete="off" value={accConfirm} placeholder="Type it again"
                                                    onChange={e => setAccConfirm(e.target.value.replace(/\D/g, '').slice(0, 18))}
                                                    className={`${inputCls(!!accConfirm && bad)} font-mono`} />
                                                {accConfirm && (bad
                                                    ? <p className="mt-1 text-xs font-semibold text-red-700">Account numbers don’t match</p>
                                                    : <p className="mt-1 text-xs font-semibold text-emerald-800 flex items-center gap-1"><Check size={13} strokeWidth={3} />Account numbers match</p>)}
                                            </>
                                        );
                                    }
                                    if (key === 'ifsc_code' && editable.has(key)) {
                                        return ifsc.state === 'idle'
                                            ? <p className="text-xs text-slate-500">Bank and branch fill in automatically</p>
                                            : <IfscStatus state={ifsc.state} info={ifsc.info} />;
                                    }
                                    return null;
                                }}
                                submit={flow.bank_open ? { label: 'Submit bank details', onSubmit: v => saveSection('bank', v, true) } : undefined}
                                savedNote={savedNote} onSave={v => saveSection('bank', v)} />
                        )}
                        {active === 'bank' && !ackBlocked && <SectionAck flow={flow} section="bank" busy={saving} onAcknowledge={acknowledge} />}
                        {active === 'bank' && ackBlocked && ackWaiting('bank') && ackOnlyInvestor}
                        {active === 'nominees' && (
                            <>
                                {/* Each unit's acknowledgment sits in that unit's own card. */}
                                <NomineesSection token={token} flow={flow} investorAadhaar={investorAadhaar} onFlow={applyFlow} onError={m => toast(m, 'error')} onEditingChange={setNomEditing}
                                    unitAck={ackBlocked ? undefined : u => <SectionAck flow={flow} section="nominees" unit={u} compact busy={saving} onAcknowledge={acknowledge} />} />
                                {ackBlocked && unitsToAck.length > 0 && ackOnlyInvestor}
                            </>
                        )}
                        {active === 'ack' && <AckSection flow={flow} onGo={go} />}

                        {/* Bank: only once every required bank detail is saved, so the investor
                            doesn't move on to nominees with the bank step half done. */}
                        {stage !== 'DRAFT' && active !== 'ack' && s[active] !== 'needs_correction' && META[idx + 1] && !gated(META[idx + 1].key)
                            && !(META[idx + 1].key === 'ack' && s.ack === 'locked')
                            && !(active === 'bank' && (flow.bank_missing || []).length > 0) && (
                            <div className="mt-6 max-lg:mt-4 flex justify-end items-center gap-3 flex-wrap text-sm">
                                <button type="button" onClick={goNext} className="min-h-[44px] px-4 max-lg:w-full rounded-xl border border-slate-300 font-bold text-[#1B4332]">Next: {META[idx + 1].label}</button>
                            </div>
                        )}
                    </section>
                </div>
            </main>
            </div>

            {showSubmit && (active === 'nominees' || active === 'ack') && !nomEditing && (
                <div className="lg:hidden fixed bottom-0 inset-x-0 bg-white border-t border-slate-200 px-4 pt-3 pb-5 z-40">
                    <button type="button" onClick={() => submit()} disabled={!canSubmit || saving}
                        className="w-full min-h-[50px] rounded-2xl bg-[#1B4332] text-white font-bold inline-flex items-center justify-center gap-2 disabled:opacity-40">
                        <Send size={16} />{submitLabel}</button>
                    <p className="text-center text-[11px] text-slate-500 mt-1">{canSubmit ? 'Ready to submit' : 'Fill in your KYC first'} · {progress}%</p>
                </div>
            )}
        </div>
    );
};

export default InvestorProfilePage;
